import { APP_CONFIG, CLASS_IS_SHOW } from '../../shared/global/base';
import { BREAKPOINTS } from '../../shared/global/layout';
import staticOptions from './staticOptions.json';
import { createInfoWindow, createContentString } from './createInfoWindow';
import { isMenu, redirectToMenu } from '../../shared/global/navigation';
import { goFish } from '../../shared/global/api';
import { Loader as MapLoader } from '@googlemaps/js-api-loader';
import Loader from '../../shared/utils/loader';
import { dominosStore } from '../../main';
import { closeOverlay } from '../Popup';
import { ChangePopupStore } from '../PopupChangePlace/ChangePopupStore';
import { dataLayerPush } from '../../shared/global/datalayers/datalayers';
import { getAppDefaultMessage } from '../../shared/global/dictionary';
import Tabs from '../../components/Tabs';
import { getOneTrustConsentStatus } from '../../shared/utils/oneTrust/getStatusData';
import { GroupIdType } from '../../types/oneTrust';
import { subscribeToConsentChanged } from '../../shared/utils/oneTrust/subscribeToConsentChanged';
import { oneTrustIsInitialized } from '../../shared/utils/oneTrust/isInitialized';
import { ConsentManager } from '../../shared/utils/oneTrust/consentManager';


export { initAfterDynamicImport, selectMarker, initMap };

const MARKERS_REFS: MapRefs = {
    currentInfoWindow: null,
    tabs: null
};

const G_LOADER = new MapLoader({
    apiKey: APP_CONFIG.google.map,
    version: 'quarterly'
});

let localSetted = false;

function initAfterDynamicImport(dom: HTMLElement) {
    // timeout is important
    setTimeout(() => {
        if (getOneTrustConsentStatus(GroupIdType.Functional)) {
            runMap(dom);
        } else {
            dom.classList.add('m-MapAllPlaces__cookie-not-accepted');
            oneTrustIsInitialized() &&
                dom.addEventListener('click', window.OneTrust.ToggleInfoDisplay);
            ConsentManager.addCallback(e => {
                if (e.detail?.includes(GroupIdType.Functional)) {
                    dom.classList.remove('m-MapAllPlaces__cookie-not-accepted');
                    dom.removeEventListener('click', window.OneTrust.ToggleInfoDisplay);
                    runMap(dom);
                }
            });
            subscribeToConsentChanged();
        }
    }, 1000);
}

function runMap (dom: HTMLElement) {
    if (!localSetted) {
        dominosStore.localStore.getCurrentLocal().then(() => initMap(dom, () => false));
        localSetted = true;
    } else {
        initMap(dom, () => false);
    }
}

/**
 * Map startup function
 *
 * @param dom - wrapper for map
 * @param isMapOnMobilePopup should return boolean when map is in mobile mode on popup
 * @param mapId used when there are 2 maps in same time (page and popup)
 * @param popupStore null when map is not on popup in void mode
 */
function initMap(
    dom: HTMLElement,
    isMapOnMobilePopup: () => boolean,
    mapId = 0,
    popupStore: ChangePopupStore | null = null
) {
    // Loader needs to be outside map container to prevent removing it by map content
    const loading = new Loader();
    const mapWrapper = dom.parentElement;
    mapWrapper.style.position = 'relative';
    loading.show(mapWrapper);
    // Get default map options
    let config = staticOptions as MapConfig;
    // Map id, used when there are 2 maps in same time (page and popup)
    config.mapId = mapId;
    // Check config options in module and update
    const configScript = document.getElementById(dom.getAttribute('data-map-config') || 'MapAllPlacesConfig');
    if (configScript) {
        try {
            // Get initial config
            const parsedConfig = JSON.parse(configScript.innerHTML) as MapConfig;
            console.log('%cMAP CONFIG:', 'color:green', parsedConfig);

            config = Object.assign({}, config, parsedConfig);
        }
        catch (e) {
            // Config malformed
            console.log('%cMAP CONFIG: JSON konfiguracyjny zawiera błędy', 'color:red');
        }
    }
    // Load map
    G_LOADER
        .load()
        .then(() => {
            // Map in mobile mode on popup (marker clicks show local info instead redirect to local page)
            config.mapOnPopup = isMapOnMobilePopup;
            // Map on popup, no redirects after map clicks when 'void' mode
            config.closeOverlayOnLocalAction = popupStore !== null && typeof popupStore != 'undefined';

            const map = new google.maps.Map(dom, config.options);

            goFish({ api: 'list' }).then((res: LocalsList) => {
                if (res && res.Data && map) {
                    createAdditionalMapElements(res.Data, map, config, dom);

                    let errorTimeout: number;
                    dom.addEventListener('click', async (event) => {
                        const target = event.target as HTMLAnchorElement;
                        if (target.hasAttribute('data-mapallplaces-localchange')) {
                            event.preventDefault();
                            dataLayerPush('orderlayer', { evParam1: 'order - pickup', evParam2: 'pickup' });
                            const btnLoader = new Loader();
                            btnLoader.show(target, true);
                            // Clear error if needed
                            clearTimeout(errorTimeout);
                            // Set local
                            const localId = target.getAttribute('data-mapallplaces-localchange');

                            // What we have in store
                            const storeLocalId =
                                (!dominosStore.localStore.isLocalNotSet &&
                                    dominosStore.localStore.local.Id) ||
                                '';
                            const storeDeliveryType = dominosStore.localStore.deliveryType;

                            // Check address when on popup in void mode (if not it doesn't matter)
                            let addressChanged = false;
                            if (config.closeOverlayOnLocalAction) {
                                // Compare
                                const deliveryAddress = JSON.stringify({ ...popupStore.address });
                                const storeDeliveryAddress = JSON.stringify({ ...dominosStore.localStore.deliveryAddress });
                                addressChanged = storeDeliveryAddress !== deliveryAddress;
                            }

                            // Set local only when something has changed
                            if (storeLocalId !== localId || storeDeliveryType !== 0 || addressChanged) {
                                try {
                                    const response = await dominosStore.localStore.setCurrentLocal(localId, 0);
                                    btnLoader.hide();
                                    // Was fired by the 'check menu' button so local status is not important
                                    if (target.hasAttribute('data-link')) {
                                        if (storeLocalId === localId && isMenu()) {
                                            closeOverlay();
                                        } else {
                                            redirectToMenu();
                                        }
                                    }
                                    // If local was set
                                    else if (response.Status) {
                                        // Map in mode when local change action will be somewhere else
                                        if (config.closeOverlayOnLocalAction) {
                                            closeOverlay({
                                                delivery: 0,
                                                local: response.Data,
                                                address: { ...popupStore.address }
                                            });
                                        }
                                        // Standard action
                                        else {
                                            if (storeLocalId === localId && isMenu()) {
                                                closeOverlay();
                                            } else {
                                                redirectToMenu();
                                            }
                                        }
                                    }
                                    // Show error on Info widow
                                    else {
                                        showErrorOnInfoWindow(target, response.Message, errorTimeout);
                                    }
                                } catch (e) {
                                    btnLoader.hide();
                                    // If it's menu link redirect anyway
                                    if (target.hasAttribute('data-link')) {
                                        redirectToMenu();
                                    } else if (e && e.Message) {
                                        showErrorOnInfoWindow(target, e.Message, errorTimeout);
                                    }
                                }
                            }
                            // Nothing changed
                            else {
                                // Redirect to menu if it was link and we are not there
                                if (target.hasAttribute('data-link') && !isMenu()) {
                                    redirectToMenu();
                                }
                                // In special mode we need close without redirection
                                else if (config.closeOverlayOnLocalAction) {
                                    closeOverlay({
                                        delivery: 0,
                                        local: dominosStore.localStore.local,
                                        address: { ...popupStore.address }
                                    });
                                }
                                // Standard action
                                else {
                                    // Redirect to menu if it's not menu page
                                    if (!isMenu()) {
                                        redirectToMenu();
                                    }
                                    // If it is we need simple close as nothing changed
                                    else {
                                        closeOverlay();
                                    }
                                }
                            }
                        }
                        else if (target.hasAttribute('data-mapallplaces-toggleicon')) {
                            event.preventDefault();

                            const openingHoursCont = dom.querySelector('[data-js=mapallplacesopeninghours]');
                            if (openingHoursCont) {
                                openingHoursCont.classList.toggle('m-OpeningHours--visible');
                            }

                            target.classList.toggle('m-MapAllPlaces__icon-toggle--rotated');
                        }
                    });
                }
                loading.hide();
            }).catch(err => {
                console.log(err);
                loading.message(config.messages.maperror);
            });
        }).catch((e) => {
            // Loading google map failed
            console.log('%cMAP: loading google map API failed', 'color:red');
            console.log(e);
            loading.message(config.messages.maperror);
        });
}

function showErrorOnInfoWindow(target: HTMLElement, message: string, tmt: number) {
    // Show error on infoWindow
    const msgCont = target.parentNode.querySelector('[data-mapallplaces-localchangeerror]');
    if (msgCont) {
        msgCont.textContent = message || getAppDefaultMessage('errors.default');
        const parent = msgCont.parentNode as HTMLElement;
        parent.classList.add(CLASS_IS_SHOW);
        tmt = window.setTimeout(() => {
            parent.classList.remove(CLASS_IS_SHOW);
        }, 5000);
    }
}


function createMarker(singleMarker: LocalMapData, map: google.maps.Map, marker: google.maps.MarkerOptions, mapId: number) {
    if (singleMarker.Location && singleMarker.Location.Lat && singleMarker.Location.Lng) {
        return {
            instance: new google.maps.Marker({
                position: {
                    lat: singleMarker.Location.Lat,
                    lng: singleMarker.Location.Lng
                },
                map: map,
                title: singleMarker.Address || '',
                icon: marker.icon
            }),
            id: singleMarker.Location.Lat + singleMarker.Location.Lng + mapId,
            url: singleMarker.Url || ''
        }
    }

    return {
        instance: null,
        id: 0,
        url: ''
    }
}


async function createAdditionalMapElements(markersData: LocalMapData[], map: google.maps.Map, config: MapConfig, dom: HTMLElement) {
    for (let index in markersData) {
        const marker = createMarker(markersData[index], map, config.marker, config.mapId);
        if (marker.id && marker.instance) {
            MARKERS_REFS[marker.id] = marker.instance;

            const infoWindow = await createInfoWindow(markersData[index], config.infoWindow, config.mapOnPopup());
            if (infoWindow) {
                marker.instance.addListener('click', async function () {
                    const mapOnPopup = config.mapOnPopup();
                    if (mapOnPopup) {
                        let contentDiv = dom.querySelector('[data-js=mapallplacesiwcontainer]');
                        let containerContent = await createContentString(markersData[index], config.infoWindow, mapOnPopup);
                        if (contentDiv) {
                            contentDiv.remove();
                        }
                        else {
                            contentDiv = document.createElement('div');
                            contentDiv.setAttribute('data-js', 'mapallplacesiwcontainer');
                        }
                        contentDiv.innerHTML = containerContent;
                        dom.appendChild(contentDiv);

                        // Replace marker icon if was clicked before
                        if (MARKERS_REFS.currentInfoWindow) {
                            MARKERS_REFS[MARKERS_REFS.currentInfoWindow.id].setIcon(config.marker.icon);
                        }
                        // Update current
                        MARKERS_REFS.currentInfoWindow = {
                            instance: infoWindow,
                            id: marker.id,
                        };
                        setTimeout(()=>{
                            MARKERS_REFS.tabs = new Tabs(dom);
                        }, 500)
                        marker.instance.setIcon(config.markerSelected.icon);
                    }
                    else {
                        if (window.innerWidth >= parseInt(BREAKPOINTS.M)) {
                            // Close current IW if any is open
                            if (MARKERS_REFS.currentInfoWindow) {
                                MARKERS_REFS[MARKERS_REFS.currentInfoWindow.id].setIcon(config.marker.icon);
                                MARKERS_REFS.currentInfoWindow.instance.close();
                            }
                            // Update current IW status and change marker icon
                            MARKERS_REFS.currentInfoWindow = {
                                instance: infoWindow,
                                id: marker.id,
                            };
                            infoWindow.open(map, marker.instance);
                            marker.instance.setIcon(config.markerSelected.icon);
                            setTimeout(()=>{
                                MARKERS_REFS.tabs = new Tabs(dom);
                            }, 500)                        }
                        else {
                            if (marker.url) {
                                window.location.href = marker.url;
                            }
                            setTimeout(()=>{
                                MARKERS_REFS.tabs = new Tabs(dom);
                            }, 500)                        }
                    }
                });
                infoWindow.addListener('closeclick', () => {
                    MARKERS_REFS.currentInfoWindow = null;
                    marker.instance.setIcon(config.marker.icon);
                });
            }
        }
    }
}

function selectMarker(lat: number, lng: number, mapId = 0) {
    if (lat && lng) {
        const latlngIdentifier = lat + lng + mapId;
        if (MARKERS_REFS && MARKERS_REFS[latlngIdentifier]) {
            google.maps.event.trigger(MARKERS_REFS[latlngIdentifier], 'click');
            return true;
        }
    }

    return false;
}
